import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Radio, Wifi, WifiOff, Plus, Trash2, Settings, RefreshCw,
  Thermometer, Droplets, Wind, Gauge, CloudRain, Sun,
  Battery, Signal, MapPin, Clock, CheckCircle, AlertCircle,
  ChevronDown, ChevronUp, Link2, Unlink
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PersonalWeatherStation({ token, lat, lon, isDarkMode }) {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [expandedStation, setExpandedStation] = useState(null);
  const [newStation, setNewStation] = useState({
    name: "",
    station_id: "",
    api_key: "",
    provider: "weatherunderground"
  });
  const [addingStation, setAddingStation] = useState(false);
  const [nearbyStations, setNearbyStations] = useState([]);
  const [loadingNearby, setLoadingNearby] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      fetchUserStations();
    }
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (lat && lon) {
      fetchNearbyStations();
    }
  }, [lat, lon]);

  const fetchUserStations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/weather/pws/stations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStations(response.data.stations || []);
    } catch (error) {
      console.error("Failed to fetch PWS stations:", error);
      // Set demo stations if API fails
      setStations([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchNearbyStations = async () => {
    setLoadingNearby(true);
    try {
      const response = await axios.get(`${API}/weather/pws/nearby`, {
        params: { lat, lon, radius: 25 }
      });
      setNearbyStations(response.data.stations || []);
    } catch (error) {
      console.error("Failed to fetch nearby stations:", error);
      setNearbyStations([]);
    } finally {
      setLoadingNearby(false);
    }
  };

  const addStation = async () => {
    if (!newStation.name || !newStation.station_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    setAddingStation(true);
    try {
      const response = await axios.post(`${API}/weather/pws/stations`, newStation, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success("Weather station added successfully!");
        setShowAddDialog(false);
        setNewStation({ name: "", station_id: "", api_key: "", provider: "weatherunderground" });
        fetchUserStations();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add station");
    } finally {
      setAddingStation(false);
    }
  };

  const removeStation = async (stationId) => {
    try {
      await axios.delete(`${API}/weather/pws/stations/${stationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Station removed");
      fetchUserStations();
    } catch (error) {
      toast.error("Failed to remove station");
    }
  };

  const refreshStationData = async (stationId) => {
    try {
      const response = await axios.get(`${API}/weather/pws/stations/${stationId}/refresh`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Update station in list
      setStations(prev => prev.map(s => 
        s.id === stationId ? { ...s, ...response.data } : s
      ));
      toast.success("Data refreshed");
    } catch (error) {
      toast.error("Failed to refresh data");
    }
  };

  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return "Unknown";
    const diff = Date.now() - new Date(timestamp).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  const getSignalStrength = (lastUpdate) => {
    if (!lastUpdate) return 0;
    const mins = (Date.now() - new Date(lastUpdate).getTime()) / 60000;
    if (mins < 5) return 3; // Strong
    if (mins < 15) return 2; // Medium
    if (mins < 60) return 1; // Weak
    return 0; // Offline
  };

  const providers = [
    { id: "weatherunderground", name: "Weather Underground", icon: "🌐" },
    { id: "ambient", name: "Ambient Weather Network", icon: "🌡️" },
    { id: "ecowitt", name: "Ecowitt", icon: "📡" },
    { id: "davis", name: "Davis Instruments", icon: "🛰️" },
    { id: "netatmo", name: "Netatmo", icon: "🏠" }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-xl ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
            <Radio className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold">Personal Weather Stations</h3>
            <p className="text-xs opacity-60">Connect your PWS or view nearby NWS stations</p>
          </div>
        </div>
        <Button
          onClick={() => setShowAddDialog(true)}
          size="sm"
          className="bg-green-500 hover:bg-green-600"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Station
        </Button>
      </div>

      {/* Your Stations */}
      {stations.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium opacity-70">Your Stations</h4>
          {stations.map((station) => {
            const signal = getSignalStrength(station.last_update);
            const isExpanded = expandedStation === station.id;
            
            return (
              <motion.div
                key={station.id}
                layout
                className={`rounded-xl border ${isDarkMode ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}
              >
                <button
                  onClick={() => setExpandedStation(isExpanded ? null : station.id)}
                  className="w-full p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                      signal > 0 ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {signal > 0 ? (
                        <Wifi className="w-5 h-5 text-green-400" />
                      ) : (
                        <WifiOff className="w-5 h-5 text-red-400" />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="font-medium">{station.name}</p>
                      <p className="text-xs opacity-50">ID: {station.station_id}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Quick Stats */}
                    {station.current && (
                      <div className="hidden md:flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Thermometer className="w-4 h-4 text-red-400" />
                          {station.current.temp}°F
                        </span>
                        <span className="flex items-center gap-1">
                          <Droplets className="w-4 h-4 text-blue-400" />
                          {station.current.humidity}%
                        </span>
                        <span className="flex items-center gap-1">
                          <Wind className="w-4 h-4 text-cyan-400" />
                          {station.current.wind_speed} mph
                        </span>
                      </div>
                    )}
                    
                    {/* Signal Indicator */}
                    <div className="flex items-center gap-1">
                      {[1, 2, 3].map(level => (
                        <div
                          key={level}
                          className={`w-1 rounded-full ${
                            signal >= level 
                              ? level === 3 ? 'h-4 bg-green-400' : level === 2 ? 'h-3 bg-green-400' : 'h-2 bg-green-400'
                              : level === 3 ? 'h-4 bg-gray-600' : level === 2 ? 'h-3 bg-gray-600' : 'h-2 bg-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 opacity-50" />
                    ) : (
                      <ChevronDown className="w-5 h-5 opacity-50" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-white/10"
                    >
                      <div className="p-4 space-y-4">
                        {/* Full Current Conditions */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-gray-100'}`}>
                            <div className="flex items-center gap-2 text-xs opacity-60 mb-1">
                              <Thermometer className="w-3 h-3" />
                              Temperature
                            </div>
                            <p className="text-xl font-bold">{station.current?.temp || "--"}°F</p>
                            <p className="text-xs opacity-50">Feels: {station.current?.feels_like || "--"}°F</p>
                          </div>
                          
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-gray-100'}`}>
                            <div className="flex items-center gap-2 text-xs opacity-60 mb-1">
                              <Droplets className="w-3 h-3" />
                              Humidity
                            </div>
                            <p className="text-xl font-bold">{station.current?.humidity || "--"}%</p>
                            <p className="text-xs opacity-50">Dew: {station.current?.dew_point || "--"}°F</p>
                          </div>
                          
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-gray-100'}`}>
                            <div className="flex items-center gap-2 text-xs opacity-60 mb-1">
                              <Wind className="w-3 h-3" />
                              Wind
                            </div>
                            <p className="text-xl font-bold">{station.current?.wind_speed || "--"} mph</p>
                            <p className="text-xs opacity-50">Gust: {station.current?.wind_gust || "--"} mph</p>
                          </div>
                          
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-gray-100'}`}>
                            <div className="flex items-center gap-2 text-xs opacity-60 mb-1">
                              <Gauge className="w-3 h-3" />
                              Pressure
                            </div>
                            <p className="text-xl font-bold">{station.current?.pressure || "--"} inHg</p>
                            <p className="text-xs opacity-50">{station.current?.pressure_trend || "Steady"}</p>
                          </div>
                          
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-gray-100'}`}>
                            <div className="flex items-center gap-2 text-xs opacity-60 mb-1">
                              <CloudRain className="w-3 h-3" />
                              Rain Today
                            </div>
                            <p className="text-xl font-bold">{station.current?.rain_today || "0"}&quot;</p>
                            <p className="text-xs opacity-50">Rate: {station.current?.rain_rate || "0"}&quot;/hr</p>
                          </div>
                          
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-gray-100'}`}>
                            <div className="flex items-center gap-2 text-xs opacity-60 mb-1">
                              <Sun className="w-3 h-3" />
                              Solar/UV
                            </div>
                            <p className="text-xl font-bold">{station.current?.solar || "--"} W/m²</p>
                            <p className="text-xs opacity-50">UV Index: {station.current?.uv || "--"}</p>
                          </div>
                        </div>

                        {/* Station Info & Actions */}
                        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-white/10">
                          <div className="flex items-center gap-4 text-xs opacity-60">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Updated: {formatLastUpdate(station.last_update)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Link2 className="w-3 h-3" />
                              {providers.find(p => p.id === station.provider)?.name || station.provider}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                refreshStationData(station.id);
                              }}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-400 hover:text-red-300"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeStation(station.id);
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Nearby Public Stations */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium opacity-70">Nearby NWS Observation Stations</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchNearbyStations}
            disabled={loadingNearby}
          >
            <RefreshCw className={`w-4 h-4 ${loadingNearby ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        
        {loadingNearby ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin opacity-50" />
          </div>
        ) : nearbyStations.length > 0 ? (
          <div className="grid gap-3 md:grid-cols-3">
            {nearbyStations.map((station) => (
              <motion.div
                key={station.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-sm">{station.name}</p>
                    <p className="text-xs opacity-50 flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {station.distance} mi away
                    </p>
                    {station.description && (
                      <p className="text-xs opacity-60 mt-0.5">{station.description}</p>
                    )}
                  </div>
                  <div className={`px-2 py-1 rounded text-xs ${
                    getSignalStrength(station.last_update) > 1 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {formatLastUpdate(station.last_update)}
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-lg font-bold">{station.temp != null ? `${station.temp}°` : '--'}</p>
                    <p className="text-xs opacity-50">Temp</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{station.humidity != null ? `${station.humidity}%` : '--'}</p>
                    <p className="text-xs opacity-50">Humidity</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{station.wind_speed != null ? station.wind_speed : '--'}</p>
                    <p className="text-xs opacity-50">Wind mph</p>
                  </div>
                </div>

                {(station.pressure || station.dew_point) && (
                  <div className="grid grid-cols-2 gap-2 text-center mt-2 pt-2 border-t border-white/10">
                    {station.pressure && (
                      <div>
                        <p className="text-sm font-semibold">{station.pressure} inHg</p>
                        <p className="text-xs opacity-50">Pressure</p>
                      </div>
                    )}
                    {station.dew_point && (
                      <div>
                        <p className="text-sm font-semibold">{station.dew_point}°F</p>
                        <p className="text-xs opacity-50">Dew Point</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-2 pt-2 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs opacity-40 uppercase tracking-wide">{station.provider || 'NWS'}</span>
                  <span className="text-xs opacity-40">{station.id}</span>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 opacity-50">
            <Radio className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">No nearby stations found</p>
          </div>
        )}
      </div>

      {/* Add Station Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className={isDarkMode ? 'bg-[#1a1a1a] border-white/10' : ''}>
          <DialogHeader>
            <DialogTitle>Add Personal Weather Station</DialogTitle>
            <DialogDescription>
              Connect your weather station to get hyper-local data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm opacity-70 mb-1 block">Station Name</label>
              <Input
                placeholder="e.g., Backyard Station"
                value={newStation.name}
                onChange={(e) => setNewStation(prev => ({ ...prev, name: e.target.value }))}
                className={isDarkMode ? 'bg-black/30' : ''}
              />
            </div>
            
            <div>
              <label className="text-sm opacity-70 mb-1 block">Provider</label>
              <select
                value={newStation.provider}
                onChange={(e) => setNewStation(prev => ({ ...prev, provider: e.target.value }))}
                className={`w-full p-2 rounded-lg border ${isDarkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'}`}
              >
                {providers.map(p => (
                  <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm opacity-70 mb-1 block">Station ID</label>
              <Input
                placeholder="e.g., KFLGAINE123"
                value={newStation.station_id}
                onChange={(e) => setNewStation(prev => ({ ...prev, station_id: e.target.value }))}
                className={isDarkMode ? 'bg-black/30' : ''}
              />
              <p className="text-xs opacity-50 mt-1">Find this in your station&apos;s dashboard</p>
            </div>
            
            <div>
              <label className="text-sm opacity-70 mb-1 block">API Key (Optional)</label>
              <Input
                placeholder="Your API key if required"
                type="password"
                value={newStation.api_key}
                onChange={(e) => setNewStation(prev => ({ ...prev, api_key: e.target.value }))}
                className={isDarkMode ? 'bg-black/30' : ''}
              />
              <p className="text-xs opacity-50 mt-1">Required for some providers</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={addStation}
              disabled={addingStation}
              className="bg-green-500 hover:bg-green-600"
            >
              {addingStation ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>Add Station</>  
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Empty State */}
      {stations.length === 0 && !loading && (
        <div className={`p-6 rounded-xl text-center ${isDarkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
          <Radio className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <h4 className="font-medium mb-1">No Stations Connected</h4>
          <p className="text-sm opacity-60 mb-4">
            Connect your personal weather station to get hyper-local weather data
          </p>
          <Button onClick={() => setShowAddDialog(true)} className="bg-green-500 hover:bg-green-600">
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Station
          </Button>
        </div>
      )}
    </div>
  );
}
